import { useCallback, useMemo, useRef, useState } from 'react'
import type { JSX } from 'react'

import { useQuery } from '@apollo/client/react'
import { AnimatePresence } from 'motion/react'
import type { Transition } from 'motion/react'
import * as m from 'motion/react-m'

import { QUERY_ACCOUNT } from '@buffer-mono/app-shell/src/common/graphql/account'
import { useSplitEnabled } from '@buffer-mono/features'
import { Loader } from '@buffer-mono/popcorn'

import { useCta } from '~publish/hooks/useCta'

import { IntroductionStep } from './components/IntroductionStep/IntroductionStep'
import { LoadingPhase } from './components/LoadingPhase/LoadingPhase'
import { OnboardingHeader } from './components/OnboardingHeader/OnboardingHeader'
import { StepRenderer } from './components/StepRenderer/StepRenderer'
import { TourStep } from './components/TourStep/TourStep'
import { useOnboardingFlow } from './useOnboardingFlow'

import styles from './OnboardingPage.module.css'

type SlideDirection = 'initial' | 'forward' | 'backward' | 'fade'
type SlideVariant = { x: number; opacity: number }

const SLIDE_OFFSET = 32
const SKIP_ALL_EXPERIMENT = 'geid-onboarding-skip-all'
const SKIP_ALL_CTA = 'publish-onboardingSurvey-header-skipAll-1'

const slideVariants = {
  enter: (direction: SlideDirection): SlideVariant => ({
    x:
      direction === 'forward'
        ? SLIDE_OFFSET
        : direction === 'backward'
          ? -SLIDE_OFFSET
          : 0,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: SlideDirection): SlideVariant => ({
    x:
      direction === 'forward'
        ? -SLIDE_OFFSET
        : direction === 'backward'
          ? SLIDE_OFFSET
          : 0,
    opacity: 0,
  }),
}

const slideTransition: Transition = { duration: 0.45, ease: 'easeInOut' }

export function OnboardingPage(): JSX.Element {
  const { data } = useQuery(QUERY_ACCOUNT, { errorPolicy: 'all' })
  const flow = useOnboardingFlow()

  const [loadingComplete, setLoadingComplete] = useState(false)
  const hasSeenLoadingPhase = useRef(false)
  // Use a ref so setting direction doesn't re-render the current step —
  // the value is read when the new step renders (on key change).
  const directionRef = useRef<SlideDirection>('initial')
  const firstName = data?.account?.name

  const showLoading =
    flow.step?.__typename === 'TourStep' &&
    !loadingComplete &&
    !hasSeenLoadingPhase.current

  // Derive current survey step index from how many survey steps are in history.
  const surveyStepIndex = flow.stepHistory.filter(
    (s) => s.__typename === 'SurveyStep',
  ).length

  const handleLoadingComplete = useCallback((): void => {
    hasSeenLoadingPhase.current = true
    setLoadingComplete(true)
  }, [])

  const handleSubmit = useCallback(
    (answers: string[], textValue?: string): void => {
      directionRef.current = 'forward'
      void flow.submitStep(answers, textValue)
    },
    [flow],
  )

  const handleSkip = useCallback((): void => {
    directionRef.current = 'forward'
    void flow.skipStep()
  }, [flow])

  const handleSkipAll = useCallback((): void => {
    directionRef.current = 'forward'
    void flow.skipAll()
  }, [flow])

  const handleGoBack = useCallback((): void => {
    // Going back from a TourStep fades rather than slides — the tour sits outside
    // the survey flow visually, so a directional slide feels wrong here.
    directionRef.current =
      flow.step?.__typename === 'TourStep' ? 'fade' : 'backward'
    flow.goBack()
  }, [flow])

  if (!flow.initialized) {
    return (
      <div className={`${styles.page} ${styles.loading}`}>
        <Loader />
      </div>
    )
  }

  if (flow.step?.__typename === 'IntroductionStep') {
    return (
      <OnboardingLayout
        onBack={handleGoBack}
        canGoBack={false}
        stepKey="introduction"
        direction={directionRef.current}
      >
        <IntroductionStep
          title={flow.step.title}
          buttonText={flow.step.buttonText}
          firstName={firstName}
          loading={flow.loading}
          onStart={(): void => handleSubmit([])}
        />
      </OnboardingLayout>
    )
  }

  if (showLoading) {
    return (
      <OnboardingLayout
        onBack={handleGoBack}
        canGoBack={false}
        stepKey="loading"
        direction="fade"
      >
        <LoadingPhase onComplete={handleLoadingComplete} />
      </OnboardingLayout>
    )
  }

  if (flow.step?.__typename === 'SurveyStep') {
    return (
      <SurveyOnboardingLayout
        onBack={handleGoBack}
        canGoBack={flow.canGoBack}
        stepKey={flow.step.slug}
        direction={directionRef.current}
        progress={{ current: surveyStepIndex, total: flow.totalSurveySteps }}
        loading={flow.loading}
        onSkipAll={handleSkipAll}
        skipAllLoading={flow.skipAllLoading}
        flowSlug={flow.flowSlug}
        flowVersion={flow.flowVersion}
        questionSlug={flow.step.slug}
        questionIndex={surveyStepIndex + 1}
      >
        <StepRenderer
          step={flow.step}
          initialAnswers={flow.initialAnswers}
          initialTextValue={flow.initialTextValue}
          disabled={flow.loading}
          loading={flow.loading && !flow.skipAllLoading}
          error={flow.error}
          onDismissError={flow.clearError}
          onSubmit={handleSubmit}
          onSkip={handleSkip}
        />
      </SurveyOnboardingLayout>
    )
  }

  if (flow.step?.__typename === 'TourStep') {
    return (
      <OnboardingLayout
        onBack={handleGoBack}
        canGoBack={flow.canGoBack}
        stepKey={flow.step.slug}
        direction="fade"
      >
        <TourStep
          step={flow.step}
          loading={flow.loading}
          error={flow.error}
          onDismissError={flow.clearError}
          onSubmit={handleSubmit}
          flowSlug={flow.flowSlug}
          flowVersion={flow.flowVersion}
        />
      </OnboardingLayout>
    )
  }

  return <></>
}

type Progress = { current: number; total: number }

function OnboardingLayout({
  onBack,
  canGoBack,
  stepKey,
  direction,
  progress,
  loading,
  onSkipAll,
  skipAllLoading,
  children,
}: {
  onBack: () => void
  canGoBack: boolean
  stepKey?: string
  direction: SlideDirection
  progress?: Progress
  loading?: boolean
  onSkipAll?: () => void
  skipAllLoading?: boolean
  children: React.ReactNode
}): JSX.Element {
  return (
    <div className={styles.page}>
      <OnboardingHeader
        onBack={onBack}
        canGoBack={canGoBack}
        progress={progress}
        loading={loading}
        onSkipAll={onSkipAll}
        skipAllLoading={skipAllLoading}
      />
      <div className={styles.container}>
        <AnimatePresence mode="popLayout" custom={direction}>
          <m.div
            key={stepKey}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={slideTransition}
            className={styles.slide}
          >
            {children}
          </m.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

type SurveyOnboardingLayoutProps = Omit<
  React.ComponentProps<typeof OnboardingLayout>,
  'onSkipAll'
> & {
  onSkipAll: () => void
  flowSlug: string | null
  flowVersion: number | null
  questionSlug: string
  questionIndex: number
}

function SurveyOnboardingLayout({
  onSkipAll,
  flowSlug,
  flowVersion,
  questionSlug,
  questionIndex,
  ...layoutProps
}: SurveyOnboardingLayoutProps): JSX.Element {
  const { isEnabled, isReady } = useSplitEnabled(SKIP_ALL_EXPERIMENT)
  const extraProperties = useMemo(
    () => ({ flowSlug, flowVersion, questionSlug, questionIndex }),
    [flowSlug, flowVersion, questionIndex, questionSlug],
  )
  const { clicked: trackSkipAllClicked } = useCta(SKIP_ALL_CTA, {
    extraProperties,
  })

  const handleSkipAll = useCallback((): void => {
    trackSkipAllClicked()
    onSkipAll()
  }, [onSkipAll, trackSkipAllClicked])

  return (
    <OnboardingLayout
      {...layoutProps}
      onSkipAll={isReady && isEnabled ? handleSkipAll : undefined}
    />
  )
}
